import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { throwErrorMessage } from "../../../helpers/kwikui.common.helpers";
import { DEFAULT_VALUES, VALIDATE_KEY_VALUES } from "./toaster.constants";
import { isValidKeyValue } from "./toaster.validation";

@Injectable({ providedIn: "root" })
export class KwikUIToasterService {
  showToasterSubject = new BehaviorSubject<boolean>(false);

  showToaster: Observable<any> = this.showToasterSubject.asObservable();

  duration: number = DEFAULT_VALUES.duration;

  message: string = DEFAULT_VALUES.message;

  position: string = DEFAULT_VALUES.position;

  // For custom positioning
  customPositionStyles: { [key: string]: string } = null;

  offset: { x: number; y: number } = DEFAULT_VALUES.offset;

  shape: string = DEFAULT_VALUES.shape;

  size: string = DEFAULT_VALUES.size;

  type: string = DEFAULT_VALUES.type;

  id: string = DEFAULT_VALUES.id;

  // Custom icons for different toaster types
  customIcons: {
    info?: string;
    success?: string;
    warning?: string;
    error?: string;
  } = {
    info: null,
    success: null,
    warning: null,
    error: null
  };

  isShowToaster = false;

  private autoDismissTimer: any;

  constructor() {
    for (const inputs of Object.entries(VALIDATE_KEY_VALUES)) {
      const key: string = inputs[0];
      const validate: any = inputs[1];

      if (validate) {
        this.validateInputProperty(key, this[key]);
      }
    }
  }

  private validateInputProperty(key: string, value: any): void {
    if (VALIDATE_KEY_VALUES[key] && !isValidKeyValue(key, value)) {
      this[key] = DEFAULT_VALUES[key];
      throwErrorMessage(
        "kwikui-toaster",
        this.id,
        key,
        value,
        DEFAULT_VALUES[key]
      );
    }
  }

  show(options?: {
    message?: string;
    type?: string;
    position?: string;
    duration?: number;
    shape?: string;
    size?: string;
    offset?: { x: number; y: number };
    customIcons?: {
      info?: string;
      success?: string;
      warning?: string;
      error?: string;
    };
  }): boolean {
    this.clearAutoDismissTimer();

    this.showToasterSubject.next(true);
    this.isShowToaster = true;

    // Reset custom positioning
    this.customPositionStyles = null;

    if (options) {
      this.message = options.message || this.message;
      this.type = options.type || this.type;
      this.position = options.position || this.position;
      this.duration = options.duration || this.duration;
      this.shape = options.shape || this.shape;
      this.size = options.size || this.size;

      // Update offset if provided
      if (options.offset) {
        this.offset = options.offset;
      }

      // Update custom icons if provided
      if (options.customIcons) {
        this.customIcons = {
          info:
            options.customIcons.info !== undefined
              ? options.customIcons.info
              : this.customIcons.info,
          success:
            options.customIcons.success !== undefined
              ? options.customIcons.success
              : this.customIcons.success,
          warning:
            options.customIcons.warning !== undefined
              ? options.customIcons.warning
              : this.customIcons.warning,
          error:
            options.customIcons.error !== undefined
              ? options.customIcons.error
              : this.customIcons.error
        };
      }

      // Calculate position based on standard position + offset
      this.calculatePositionWithOffset(this.offset.x, this.offset.y);
    }

    this.startAutoDismissTimer();

    return this.isShowToaster;
  }

  hide(): boolean {
    this.clearAutoDismissTimer();
    this.isShowToaster = false;
    this.showToasterSubject.next(false);

    return this.isShowToaster;
  }

  /**
   * Starts auto-dismiss timer based on duration
   */
  private startAutoDismissTimer(): void {
    if (this.duration && this.duration > 0) {
      this.autoDismissTimer = setTimeout(() => {
        this.hide();
      }, this.duration);
    }
  }

  private clearAutoDismissTimer(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
  }

  /**
   * Calculates position with x and y offsets relative to standard position
   */
  private calculatePositionWithOffset(xOffset: number, yOffset: number): void {
    this.customPositionStyles = {
      position: "fixed"
    };

    // Base position based on standard position
    switch (this.position) {
      case "top-left":
        this.customPositionStyles.top = `calc(var(--kwikui-space-m) + ${yOffset}px)`;
        this.customPositionStyles.left = `calc(var(--kwikui-space-m) + ${xOffset}px)`;
        break;
      case "top-center":
        this.customPositionStyles.top = `calc(var(--kwikui-space-m) + ${yOffset}px)`;
        this.customPositionStyles.left = `calc(50% + ${xOffset}px)`;
        this.customPositionStyles.transform = "translateX(-50%)";
        break;
      case "top-right":
        this.customPositionStyles.top = `calc(var(--kwikui-space-m) + ${yOffset}px)`;
        this.customPositionStyles.right = `calc(var(--kwikui-space-m) - ${xOffset}px)`;
        break;
      case "center-left":
        this.customPositionStyles.top = `calc(50% + ${yOffset}px)`;
        this.customPositionStyles.left = `calc(var(--kwikui-space-m) + ${xOffset}px)`;
        this.customPositionStyles.transform = "translateY(-50%)";
        break;
      case "center-center":
        this.customPositionStyles.top = `calc(50% + ${yOffset}px)`;
        this.customPositionStyles.left = `calc(50% + ${xOffset}px)`;
        this.customPositionStyles.transform = "translate(-50%, -50%)";
        break;
      case "center-right":
        this.customPositionStyles.top = `calc(50% + ${yOffset}px)`;
        this.customPositionStyles.right = `calc(var(--kwikui-space-m) - ${xOffset}px)`;
        this.customPositionStyles.transform = "translateY(-50%)";
        break;
      case "bottom-left":
        this.customPositionStyles.bottom = `calc(var(--kwikui-space-m) - ${yOffset}px)`;
        this.customPositionStyles.left = `calc(var(--kwikui-space-m) + ${xOffset}px)`;
        break;
      case "bottom-center":
        this.customPositionStyles.bottom = `calc(var(--kwikui-space-m) - ${yOffset}px)`;
        this.customPositionStyles.left = `calc(50% + ${xOffset}px)`;
        this.customPositionStyles.transform = "translateX(-50%)";
        break;
      case "bottom-right":
        this.customPositionStyles.bottom = `calc(var(--kwikui-space-m) - ${yOffset}px)`;
        this.customPositionStyles.right = `calc(var(--kwikui-space-m) - ${xOffset}px)`;
        break;
      default:
        // Default positioning
        this.customPositionStyles.top = `${yOffset}px`;
        this.customPositionStyles.left = `${xOffset}px`;
    }
  }
}
